export type RequestCredentials = "omit" | "same-origin" | "include";

export type RequestConfig<TData = unknown> = {
  baseURL?: string;
  url?: string;
  method?: "GET" | "PUT" | "PATCH" | "POST" | "DELETE" | "OPTIONS" | "HEAD";
  params?: unknown;
  data?: TData | FormData;
  responseType?:
    | "arraybuffer"
    | "blob"
    | "document"
    | "json"
    | "text"
    | "stream";
  signal?: AbortSignal;
  headers?: [string, string][] | Record<string, string>;
  credentials?: RequestCredentials;
  keepalive?: boolean;
};

export type ResponseConfig<TData = unknown> = {
  data: TData;
  status: number;
  statusText: string;
  headers: Headers;
};

export type ResponseErrorConfig<TError = unknown> = TError;

export type Client = <TData, _TError = unknown, TVariables = unknown>(
  config: RequestConfig<TVariables>,
) => Promise<ResponseConfig<TData>>;

let _config: Partial<RequestConfig> = {};

export const getConfig = () => _config;

export const setConfig = (config: Partial<RequestConfig>) => {
  _config = config;
  return _config;
};

export const mergeConfig = <T extends RequestConfig>(
  ...configs: Array<Partial<T>>
): Partial<T> =>
  configs.reduce<Partial<T>>(
    (merged, config) => ({
      ...merged,
      ...config,
      headers: {
        ...(Array.isArray(merged.headers)
          ? Object.fromEntries(merged.headers)
          : merged.headers),
        ...(Array.isArray(config.headers)
          ? Object.fromEntries(config.headers)
          : config.headers),
      },
    }),
    {},
  );

const baseClient = async <TData, _TError = unknown, TVariables = unknown>(
  paramsConfig: RequestConfig<TVariables>,
): Promise<ResponseConfig<TData>> => {
  const config = mergeConfig(getConfig(), paramsConfig);

  const normalizedParams = new URLSearchParams();
  Object.entries((config.params as Record<string, unknown>) ?? {}).forEach(
    ([key, value]) => {
      if (value !== undefined) {
        normalizedParams.append(key, value === null ? "null" : String(value));
      }
    },
  );

  let targetUrl = [config.baseURL, config.url].filter(Boolean).join("");
  if (config.params) targetUrl += `?${normalizedParams}`;

  const formData = config.data instanceof FormData ? config.data : undefined;
  const callerHeaders = Array.isArray(config.headers)
    ? Object.fromEntries(config.headers)
    : (config.headers ?? {});

  // Headers.set() replaces any existing entry case-insensitively, unlike spreading plain
  // objects, which would keep both "Content-Type" and "content-type" as separate keys and
  // have fetch combine their values into one invalid media type.
  const headers = new Headers();
  Object.entries(callerHeaders).forEach(([name, value]) =>
    headers.set(name, value),
  );

  // FormData must carry no Content-Type so fetch can set multipart/form-data with its own
  // boundary; every other request keeps the default JSON content type it always had, unless
  // the caller already set one under a different casing.
  if (!formData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await globalThis.fetch(targetUrl, {
    credentials: config.credentials ?? "same-origin",
    method: config.method?.toUpperCase(),
    body: formData ?? JSON.stringify(config.data),
    signal: config.signal,
    keepalive: config.keepalive,
    headers,
  });

  const noBody = [204, 205, 304].includes(response.status) || !response.body;

  if (!response.ok) {
    const errorData = noBody ? {} : await response.json().catch(() => null);
    throw Object.assign(new Error(response.statusText), {
      data: errorData,
      status: response.status,
    });
  }

  const data = noBody ? {} : await response.json();

  return {
    data: data as TData,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  };
};

export const createFetchClient = (baseURL: string) =>
  Object.assign(
    <TData, _TError = unknown, TVariables = unknown>(
      config: RequestConfig<TVariables>,
    ): Promise<ResponseConfig<TData>> =>
      baseClient<TData, _TError, TVariables>({ ...config, baseURL }),
    { setConfig, getConfig },
  );
